// src/pages/FacilityDetails.jsx

import {
    useEffect,
    useState,
    useCallback,
    useMemo,
    useRef
} from "react";

import {
    useParams,
    useNavigate
} from "react-router-dom";

import {
    ArrowLeft,
    Pencil
} from "lucide-react";

import FacilityMap from "../components/FacilityMap";
import FacilityStats from "../components/FacilityStats";

import {
    getFacilityDetailsAPI,
    getFacilityVehiclesAPI,
    getFacilityLayoutAPI
} from "../services/api";

import {
    mapVehicleFromAPI,
    resetParkingLayout,
    createMovingVehicle,
    createExitVehicle,
    finalizeVehicleExit,
    initializeFacilityLayout
} from "../utils/parkingUtils";

import websocketService
    from "../services/websocket";

export default function FacilityDetails() {

    const navigate = useNavigate();

    // =========================================
    // ROUTE PARAMS
    // =========================================

    const {
        facilityId: facility_id
    } = useParams();

    // =========================================
    // STATES
    // =========================================

    const [facility, setFacility] =
        useState(null);

    const [layout, setLayout] =
        useState([]);

    const [parkedVehicles, setParkedVehicles] =
        useState([]);

    const [movingVehicles, setMovingVehicles] =
        useState([]);

    const [loading, setLoading] =
        useState(true);

    const [error, setError] =
        useState("");

    const [wsConnected, setWsConnected] =
        useState(false);

    const [currentVehicles, setCurrentVehicles] =
        useState(0);

    const [totalEntries, setTotalEntries] =
        useState(0);

    const [totalExits, setTotalExits] =
        useState(0);

    // =========================================
    // REFS
    // =========================================

    const mountedRef = useRef(true);

    // =========================================
    // CLEANUP REF
    // =========================================

    useEffect(() => {

        mountedRef.current = true;

        return () => {

            mountedRef.current = false;
        };

    }, []);

    // =========================================
    // FETCH FACILITY DETAILS
    // =========================================

    const fetchFacilityDetails =
        useCallback(async () => {

            try {

                const response =
                    await getFacilityDetailsAPI(
                        facility_id
                    );

                if (!mountedRef.current) {
                    return;
                }

                setFacility(response);

                setCurrentVehicles(
                    response.current_vehicles || 0
                );

                setTotalEntries(
                    response.total_checkins_today || 0
                );

                setTotalExits(
                    response.total_checkouts_today || 0
                );

            } catch (err) {

                console.error(
                    "Facility details error:",
                    err
                );

                if (!mountedRef.current) {
                    return;
                }

                setError(
                    "Failed to load facility details"
                );
            }

        }, [facility_id]);

    // =========================================
    // FETCH FACILITY LAYOUT
    // =========================================

    const fetchFacilityLayout =
        useCallback(async () => {

            try {

                const response =
                    await getFacilityLayoutAPI(
                        facility_id
                    );

                if (!mountedRef.current) {
                    return;
                }

                const slots = response?.slots || [];
                
                setLayout(slots);

                // ========================================
                // CRITICAL: Initialize parking engine
                // with the fetched slots
                // ========================================
                initializeFacilityLayout({
                    facilityKey: facility_id,
                    slots: slots
                });

            } catch (err) {

                console.error(
                    "Facility layout error:",
                    err
                );

                if (!mountedRef.current) {
                    return;
                }

                setError(
                    "Failed to load facility layout"
                );
            }

        }, [facility_id]);

    // =========================================
    // FETCH VEHICLES
    // =========================================

    const fetchVehicles =
        useCallback(async () => {

            try {

                const response =
                    await getFacilityVehiclesAPI(
                        facility_id
                    );

                const mappedVehicles =
                    response.map((session) =>
                        mapVehicleFromAPI(
                            session,
                            facility_id
                        )
                    );

                if (!mountedRef.current) {
                    return;
                }

                setParkedVehicles(
                    mappedVehicles
                );

            } catch (err) {

                console.error(
                    "Vehicle fetch error:",
                    err
                );

                if (!mountedRef.current) {
                    return;
                }

                setError(
                    "Failed to load vehicles"
                );
            }

        }, [facility_id]);

    // =========================================
    // INITIAL LOAD
    // =========================================

    useEffect(() => {

        const loadData = async () => {

            try {

                setLoading(true);

                setError("");

                resetParkingLayout();

                // =================================
                // FETCH FACILITY + LAYOUT FIRST
                // Layout must be initialized before
                // vehicles are mapped
                // =================================

                await Promise.all([
                    fetchFacilityDetails(),
                    fetchFacilityLayout()
                ]);

                // =================================
                // FETCH VEHICLES AFTER LAYOUT
                // =================================

                await fetchVehicles();

            } catch (err) {

                console.error(
                    "Initial load error:",
                    err
                );

                if (mountedRef.current) {

                    setError(
                        "Failed to load facility"
                    );
                }

            } finally {

                if (mountedRef.current) {

                    setLoading(false);
                }
            }
        };

        loadData();

    }, [
        fetchFacilityDetails,
        fetchFacilityLayout,
        fetchVehicles
    ]);

    // =========================================
    // AUTO REFRESH STATS
    // =========================================

    useEffect(() => {

        if (!facility_id) {
            return;
        }

        const interval =
            setInterval(() => {

                fetchFacilityDetails();

            }, 30000);

        return () => {

            clearInterval(interval);
        };

    }, [
        facility_id,
        fetchFacilityDetails
    ]);

    // =========================================
    // WEBSOCKET
    // =========================================

    useEffect(() => {

        if (!facility_id) {
            return;
        }

        const token =
            sessionStorage.getItem(
                "token"
            );

        websocketService.connect(
            facility_id,
            token
        );

        // =====================================
        // CONNECTED
        // =====================================

        const handleConnected = () => {

            setWsConnected(true);
        };

        // =====================================
        // DISCONNECTED
        // =====================================

        const handleDisconnected = () => {

            setWsConnected(false);
        };

        // =====================================
        // VEHICLE ENTRY
        // =====================================

        const handleVehicleEntry =
            (data) => {

                const movingVehicle =
                    createMovingVehicle(
                        data,
                        facility_id
                    );

                setMovingVehicles(
                    (prev) => {

                        const exists =
                            prev.some(
                                (v) =>
                                    v.id ===
                                    movingVehicle.id
                            );

                        if (exists) {
                            return prev;
                        }

                        return [
                            ...prev,
                            movingVehicle
                        ];
                    }
                );

                setTimeout(() => {

                    if (!mountedRef.current) {
                        return;
                    }

                    setMovingVehicles(
                        (prev) =>
                            prev.filter(
                                (v) =>
                                    v.id !==
                                    movingVehicle.id
                            )
                    );

                    setParkedVehicles(
                        (prev) => {

                            const exists =
                                prev.some(
                                    (v) =>
                                        v.id ===
                                        movingVehicle.id
                                );

                            if (exists) {
                                return prev;
                            }

                            return [
                                ...prev,
                                {
                                    ...movingVehicle,
                                    status:
                                        "parked"
                                }
                            ];
                        }
                    );

                }, 3000);
            };

        // =====================================
        // VEHICLE EXIT
        // =====================================

        const handleVehicleExit =
            (data) => {

                const exitVehicle =
                    createExitVehicle(
                        data,
                        facility_id
                    );

                setParkedVehicles(
                    (prev) =>
                        prev.filter(
                            (v) =>
                                v.id !==
                                exitVehicle.id
                        )
                );

                setMovingVehicles(
                    (prev) => [
                        ...prev,
                        exitVehicle
                    ]
                );

                finalizeVehicleExit({

                    vehicleId:
                        exitVehicle.id,

                    facilityKey:
                        exitVehicle.facility_key ||
                        "default"
                });

                setTimeout(() => {

                    if (!mountedRef.current) {
                        return;
                    }

                    setMovingVehicles(
                        (prev) =>
                            prev.filter(
                                (v) =>
                                    v.id !==
                                    exitVehicle.id
                            )
                    );

                }, 3000);
            };

        // =====================================
        // OCCUPANCY UPDATE
        // =====================================

        const handleOccupancyUpdate =
            (data) => {

                if (
                    data.current_vehicles !==
                    undefined
                ) {

                    setCurrentVehicles(
                        data.current_vehicles
                    );
                }

                if (
                    data.total_checkins_today !==
                    undefined
                ) {

                    setTotalEntries(
                        data.total_checkins_today
                    );
                }

                if (
                    data.total_checkouts_today !==
                    undefined
                ) {

                    setTotalExits(
                        data.total_checkouts_today
                    );
                }
            };

        websocketService.on(
            "connected",
            handleConnected
        );

        websocketService.on(
            "disconnected",
            handleDisconnected
        );

        websocketService.on(
            "vehicle_entry",
            handleVehicleEntry
        );

        websocketService.on(
            "vehicle_exit",
            handleVehicleExit
        );

        websocketService.on(
            "occupancy_update",
            handleOccupancyUpdate
        );

        return () => {

            websocketService.off(
                "connected",
                handleConnected
            );

            websocketService.off(
                "disconnected",
                handleDisconnected
            );

            websocketService.off(
                "vehicle_entry",
                handleVehicleEntry
            );

            websocketService.off(
                "vehicle_exit",
                handleVehicleExit
            );

            websocketService.off(
                "occupancy_update",
                handleOccupancyUpdate
            );

            websocketService.disconnect();
        };

    }, [facility_id]);

    // =========================================
    // RESET PARKING LAYOUT
    // =========================================

    useEffect(() => {

        return () => {

            resetParkingLayout();
        };

    }, []);

    // =========================================
    // COMPUTED VALUES
    // =========================================

    const occupancyPercentage =
        useMemo(() => {

            if (
                !facility?.total_slots ||
                facility.total_slots <= 0
            ) {
                return 0;
            }

            return Math.round(
                (
                    currentVehicles /
                    facility.total_slots
                ) * 100
            );

        }, [
            currentVehicles,
            facility
        ]);

    // =========================================
    // LOADING
    // =========================================

    if (loading) {

        return (

            <div className="
                h-screen
                flex
                items-center
                justify-center
                bg-gray-100
            ">

                <div className="
                    flex
                    flex-col
                    items-center
                    gap-4
                ">

                    <div className="
                        w-14
                        h-14
                        border-4
                        border-blue-500
                        border-t-transparent
                        rounded-full
                        animate-spin
                    " />

                    <p className="
                        text-gray-600
                        font-medium
                    ">
                        Loading facility...
                    </p>

                </div>

            </div>
        );
    }

    // =========================================
    // ERROR
    // =========================================

    if (error) {

        return (

            <div className="
                h-screen
                flex
                items-center
                justify-center
                bg-gray-100
            ">

                <div className="
                    bg-white
                    p-8
                    rounded-2xl
                    shadow-md
                    text-center
                    max-w-md
                ">

                    <h2 className="
                        text-2xl
                        font-bold
                        text-red-500
                        mb-3
                    ">
                        Error
                    </h2>

                    <p className="
                        text-gray-600
                        mb-5
                    ">
                        {error}
                    </p>

                    <button
                        onClick={() => navigate(-1)}
                        className="
                            bg-blue-600
                            hover:bg-blue-700
                            text-white
                            px-5
                            py-2
                            rounded-xl
                            transition-all
                        "
                    >
                        Go Back
                    </button>

                </div>

            </div>
        );
    }

    return (

        <div className="
            min-h-screen
            bg-gray-100
            flex
            flex-col
        ">

            {/* HEADER */}

            <div className="
                bg-white
                shadow-sm
                px-6
                py-4
                flex
                items-center
                justify-between
                border-b
                shrink-0
            ">

                <div className="
                    flex
                    items-center
                    gap-4
                ">

                    <button
                        onClick={() => navigate(-1)}
                        className="
                            p-2
                            rounded-xl
                            hover:bg-gray-100
                            transition-all
                        "
                    >
                        <ArrowLeft size={22} />
                    </button>

                    <div>

                        <h1 className="
                            text-3xl
                            font-bold
                            text-gray-800
                        ">
                            {facility?.facility_name}
                        </h1>

                        <p className="
                            text-gray-500
                            mt-1
                        ">
                            {facility?.city},{" "}
                            {facility?.state}
                        </p>

                    </div>

                </div>

                <div className="
                    flex
                    items-center
                    gap-3
                ">

                    <button
                        onClick={() =>
                            navigate(
                                `/facility/${facility_id}/layout-editor`
                            )
                        }
                        className="
                            flex
                            items-center
                            gap-2
                            bg-blue-600
                            hover:bg-blue-700
                            text-white
                            px-4
                            py-2
                            rounded-xl
                            transition-all
                            shadow-sm
                        "
                    >
                        <Pencil size={18} />
                        Edit Layout
                    </button>

                    <div className={`
                        flex
                        items-center
                        gap-2
                        px-4
                        py-2
                        rounded-full
                        text-sm
                        font-semibold
                        ${
                            wsConnected
                                ? "bg-green-100 text-green-700"
                                : "bg-red-100 text-red-700"
                        }
                    `}>

                        <div className={`
                            w-2.5
                            h-2.5
                            rounded-full
                            ${
                                wsConnected
                                    ? "bg-green-500 animate-pulse"
                                    : "bg-red-500"
                            }
                        `} />

                        {
                            wsConnected
                                ? "LIVE"
                                : "OFFLINE"
                        }

                    </div>

                </div>

            </div>

            {/* STATS */}

            <FacilityStats
                currentVehicles={
                    currentVehicles
                }
                totalEntries={
                    totalEntries
                }
                totalExits={
                    totalExits
                }
                totalSlots={
                    facility?.total_slots || 0
                }
                occupancyPercentage={
                    occupancyPercentage
                }
            />

            {/* MAP */}

            <div className="
                flex-1
                p-6
                overflow-hidden
            ">

                <FacilityMap
                    facility={{
                        ...facility,
                        slots: layout
                    }}
                    parkedVehicles={
                        parkedVehicles
                    }
                    movingVehicles={
                        movingVehicles
                    }
                />

            </div>

        </div>
    );
}